import { createStockOrderComment } from 'services/stockOrders';
import { TbordOneToneIcon } from '../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon.types';
import { noDataCopy } from '../../../constants/constants';
import { createOrderComment, createOrderCustomerChatComment } from '../../../services/orders';
import {
  createLogisticsServiceComment,
  createLogisticServiceCustomerChatComment
} from '../../../services/services';
import { BACKOFFICE_MODULES, PERMISSION_TYPE_IDS } from '../../../types/configuration.types';
import { ICountryDetail } from '../../../types/countries';
import { Ilocation } from '../../../types/global';
import { ORDER_COMMENT_TYPES_ENUM, PAYMENT_TYPES, REPORT_STATUS } from '../../../types/orders';
import { ILogisticReports, LOCATION_PLACE_TYPE, LS_REPORT_REASONS } from '../../../types/services';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { getCountryDataFromCode } from '../../../utils/getCountries';
import { getNudosData } from '../../../utils/getNudosData';
import { changeNamingConvention } from '../../../utils/namingConventionsManagement';
import { ORDER_COMMENT_TYPES_ENUM_STOCK_ORDER } from 'types/stockOrders';

export const submitComment = async ({
  logisticServiceId,
  orderId,
  stockOrderId,
  allowNewCommentsSubmission,
  comments,
  mentions,
  commentImage,
  updateDataCallback,
  handleLoading,
  kamUserId
}: {
  logisticServiceId?: number | string;
  orderId?: number | string;
  stockOrderId?: number | string;
  allowNewCommentsSubmission?: boolean;
  comments: string;
  mentions: number[];
  kamUserId?: number;
  commentImage?: File;
  updateDataCallback?: () => void;
  handleLoading?: (load: boolean) => void;
}) => {
  const { userData } = getNudosData() || {};
  const userId = userData?.id;
  if (!logisticServiceId && !orderId && !stockOrderId) return;
  if (!allowNewCommentsSubmission || !userId) return;
  handleLoading?.(true);
  try {
    if (logisticServiceId)
      await createLogisticsServiceComment({
        logisticServiceId: Number(logisticServiceId),
        userId,
        commentType: ORDER_COMMENT_TYPES_ENUM.OPS_NOTE,
        comments,
        mentions,
        commentImage,
        ...(kamUserId && { kamUserId: Number(kamUserId) })
      });

    if (orderId) {
      await createOrderComment({
        orderId: +orderId,
        userId,
        commentType: ORDER_COMMENT_TYPES_ENUM.OPS_NOTE,
        comments,
        mentions,
        commentImage,
        ...(kamUserId && { kamUserId: Number(kamUserId) })
      });
    }
    if (stockOrderId) {
      await createStockOrderComment({
        stockOrderId: +stockOrderId,
        userId,
        commentType: ORDER_COMMENT_TYPES_ENUM_STOCK_ORDER.OPS_NOTE,
        comments,
        mentions,
        commentImage
      });
    }
    updateDataCallback?.();
  } catch (error) {
    displayErrorNotification();
  } finally {
    handleLoading?.(false);
  }
};

export const submitCustomerChatComment = async ({
  logisticServiceId,
  orderId,
  userId,
  comments,
  commentImage
}: {
  logisticServiceId?: number | string;
  orderId?: number | string;
  userId: number;
  comments: string;
  commentImage?: File;
}) => {
  try {
    if (logisticServiceId) {
      await createLogisticServiceCustomerChatComment({
        logisticServiceId: Number(logisticServiceId),
        userId,
        commentType: ORDER_COMMENT_TYPES_ENUM.CUSTOMER_CHAT,
        comments,
        mentions: [],
        commentImage
      });
    }

    if (orderId) {
      await createOrderCustomerChatComment({
        orderId: +orderId,
        userId,
        commentType: ORDER_COMMENT_TYPES_ENUM.CUSTOMER_CHAT,
        comments,
        mentions: [],
        commentImage
      });
    }
  } catch (error) {
    displayErrorNotification();
  }
};

export const isModuleActive = (moduleName: string) => {
  //CAMBIAR-JC: temporalExlusions es deuda técnica, los permisos deben ser manejados en el back dinámicamente y no en el front de forma estática.
  const temporalExlusions = ['opsSupport', 'paymentRequest', 'dashboard', 'account', 'tools', 'trackingOrder'];
  if (moduleName === 'paymentRequest') return true;
  if (process.env.NODE_ENV === 'development') return true;
  const loggedUserData = getNudosData();
  const userModulePermissions = loggedUserData?.permissions?.permissions[moduleName as BACKOFFICE_MODULES];
  const temporaryExclusionsPermission = temporalExlusions?.includes(moduleName);
  const hasViewPermission = !!userModulePermissions?.includes(PERMISSION_TYPE_IDS.VIEW);
  const hasEditPermission = !!userModulePermissions?.includes(PERMISSION_TYPE_IDS.EDIT);
  const hasPermissionForThisModule = hasViewPermission || hasEditPermission || temporaryExclusionsPermission;
  return hasPermissionForThisModule;
};

/**
 * Verifica si un módulo agrupado tiene permisos activos verificando sus submódulos
 * @param moduleName - Nombre del módulo agrupado ('operations' o 'catalogGroup')
 * @param subModuleNames - Array con los nombres de los submódulos a verificar
 * @returns true si al menos uno de los submódulos tiene permisos, false en caso contrario
 */
export const isGroupModuleActive = (moduleName: string, subModuleNames: string[]): boolean => {
  return subModuleNames.some(subModuleName => isModuleActive(subModuleName));
};

export const getModuleNameFromPath = (path: string) => {
  const pathText = path ? path.replaceAll('/', '') : '';
  const moduleName = changeNamingConvention(pathText, 'kebab', 'camel');
  return moduleName;
};

export const displayDocumentByPermission = () => {
  const userData = getNudosData();
  const permissionGroupId = userData?.permissions?.permissionGroups[0]?.id;
  return !!(permissionGroupId && permissionGroupId !== 8);
};

export const isEditingInThisModuleAllowed = () => {
  const { permissions } = getNudosData() || {};
  if (!permissions?.permissions) return false;
  const path = window.location.pathname.split('/')[1];
  const moduleName = getModuleNameFromPath(path);
  const thisModulePermissions = permissions?.permissions[moduleName as BACKOFFICE_MODULES];
  const hasEditPermissions = !!thisModulePermissions?.includes(PERMISSION_TYPE_IDS.EDIT);
  return hasEditPermissions;
};

export const getLsDateLogLabel = (
  reason?: string,
  reasonGlobalStatus?: string,
  attemptNumberText?: string,
  status?: string,
  displayFullLogLabel?: boolean
) => {
  const labelPrefix = reasonGlobalStatus === 'IN_PROGRESS' ? 'Correo enviado ' : 'Actualizado ';
  const attemptNumberSufix = attemptNumberText ? ` ${attemptNumberText}` : '';
  if (displayFullLogLabel) return `${labelPrefix} - ${status}${attemptNumberSufix}`;
  switch (reason) {
    case LS_REPORT_REASONS.UNRESPONSIVE_DELIVERY_EMPLOYEE:
    case LS_REPORT_REASONS.UNRESPONSIVE_RECEIVING_EMPLOYEE:
      return `${labelPrefix} - el empleado no responde${attemptNumberSufix}`;
    case LS_REPORT_REASONS.ORIGIN_POINT_ADVERSE:
    case LS_REPORT_REASONS.DESTINATION_POINT_ADVERSE:
      return `${labelPrefix} - situación adversa${attemptNumberSufix}`;
    case LS_REPORT_REASONS.EMPLOYEE_NOT_DATE_RECEIVER:
    case LS_REPORT_REASONS.EMPLOYEE_NOT_DATE_DELIVERY:
      return `${labelPrefix} - incumplimiento de fechas${attemptNumberSufix}`;
    case LS_REPORT_REASONS.EMPLOYEE_NOT_WILLING:
      return `${labelPrefix} - empleado no entrega${attemptNumberSufix}`;
    case LS_REPORT_REASONS.HOLIDAY:
      return `${labelPrefix} - festivo en el país`;
    case LS_REPORT_REASONS.AWAIT_RESPONSE:
      return `${labelPrefix} - a la espera de respuesta`;
    case LS_REPORT_REASONS.WITHOUT_REASON:
      return `${labelPrefix} - sin razones`;
    case 'requestDate':
      return 'Solicitado';
    case 'confirmedDate':
      return 'Confirmado';
    case 'inProcessDate':
      return 'En proceso';
    case 'activationDate':
      return 'Activo';
    case 'cancelledDate':
      return 'Cancelado';
    case 'deliveryDate':
      return 'Entregado';
    case 'pending':
      return 'Cotización cargada';
    case 'approved':
      return 'Cotización aprobada';
    case 'rejected':
      return 'Cotización rechazada';
    default:
      return '';
  }
};

export const getPaymentMethodName = (paymentMethod?: string) => {
  // This is temporal, once the payment methods in back are properly handled, this util can be removed and the paymentMethodName shown directly
  if (paymentMethod === PAYMENT_TYPES.INTERNATIONAL_CARD) return 'Tarjeta de crédito USD';
  return paymentMethod || 'Sin data del método de pago';
};

export const formatLocationName = (location?: Ilocation, listOfCountries?: ICountryDetail[], employeeName?: string) => {
  const country = getCountryDataFromCode(location?.country, listOfCountries);
  switch (location?.place) {
    case LOCATION_PLACE_TYPE.USER:
      return employeeName || 'Empleado';
    case LOCATION_PLACE_TYPE.OTHER:
      return `Otra dirección: ${location?.locationName || '-'}`;
    case LOCATION_PLACE_TYPE.OFFICE:
      return `Oficina ${country?.name || ''}`;
    case LOCATION_PLACE_TYPE.NUDOS:
      return `Bodega ${country?.name || ''}`;
    case LOCATION_PLACE_TYPE.DIAGNOSTIC:
      return 'Diagnóstico';
    case LOCATION_PLACE_TYPE.WARRANTY:
      return 'Garantía';
    case LOCATION_PLACE_TYPE.STORE:
      return '.bord Store';
    default:
      return noDataCopy;
  }
};

export const getLocationIcon = ({
  place
}: {
  place: LOCATION_PLACE_TYPE;
  headerLogisticsService?: boolean;
}): TbordOneToneIcon => {
  const defaultPlace: TbordOneToneIcon = 'warehouse';
  const renderPlaceIcons: Record<LOCATION_PLACE_TYPE, TbordOneToneIcon> = {
    [LOCATION_PLACE_TYPE.NUDOS]: 'warehouse',
    [LOCATION_PLACE_TYPE.OFFICE]: 'building3',
    [LOCATION_PLACE_TYPE.OTHER]: 'helpCircle',
    [LOCATION_PLACE_TYPE.USER]: 'user',
    [LOCATION_PLACE_TYPE.WARRANTY]: 'shield',
    [LOCATION_PLACE_TYPE.DIAGNOSTIC]: 'tool',
    [LOCATION_PLACE_TYPE.STORE]: 'market',
    [LOCATION_PLACE_TYPE.BORD_STORE]: 'market'
  };

  return renderPlaceIcons[place] ?? defaultPlace;
};

export const getLatestOpenLogisticReport = (
  reports: ILogisticReports[] | undefined | null
): ILogisticReports | null => {
  const open = reports?.filter(r => r.currentStatus === REPORT_STATUS.OPEN && r.reportId != null) ?? [];
  if (open.length === 0) return null;
  return open.reduce((latest, r) => ((r.reportId ?? 0) > (latest.reportId ?? 0) ? r : latest));
};
